/**
 * Custom hook for managing responsive Input field styling.
 *
 * Consolidates all responsive prop generation in one place:
 * 1. Gets SSR-safe base values for initial render
 * 2. Generates responsive CSS class arrays for all breakpoints
 *
 * This keeps Input.tsx focused on JSX structure while responsive logic
 * is isolated, testable, and reusable for Input variants.
 *
 * @param fieldColumns - Responsive column setting (e.g., { base: 'twelve', md: 'six' })
 * @param fieldMaxWidthByChars - Responsive character width setting
 * @param inputStyles - CSS module for class name references
 * @returns Object containing base values and responsive classes for both column and width variants
 */

import {
  generateResponsiveClasses,
  getBaseValue,
} from '../../../../utils/breakpoint/responsiveSSR';
import { type InputFieldVariants } from '../../../../utils/forms/input/input';

export interface ResponsiveInputFieldConfig {
  /** SSR fallback value for columns (always a single value, not responsive) */
  baseColumns: InputFieldVariants['fieldColumns'];
  /** SSR fallback value for character-based width (always a single value, not responsive) */
  baseMaxWidthByChars: InputFieldVariants['fieldMaxWidthByChars'];
  /** Array of responsive CSS class names for column variant at each breakpoint */
  columnsResp: string[];
  /** Array of responsive CSS class names for character-width variant at each breakpoint */
  maxWidthByCharsResp: string[];
}

export const useResponsiveInputField = (
  fieldColumns:
    | Exclude<InputFieldVariants['fieldColumns'], null>
    | { [key: string]: Exclude<InputFieldVariants['fieldColumns'], null> }
    | undefined,
  fieldMaxWidthByChars:
    | Exclude<InputFieldVariants['fieldMaxWidthByChars'], null>
    | {
        [key: string]: Exclude<
          InputFieldVariants['fieldMaxWidthByChars'],
          null
        >;
      }
    | undefined,
  inputStyles: Record<string, string>,
): ResponsiveInputFieldConfig => {
  // Initialize responsive class generator with CSS module
  // This function creates CSS class names for all breakpoints
  const getResponsiveClasses = generateResponsiveClasses(inputStyles);

  // Get SSR-safe base values for initial server render
  // Always use 'twelve' (full width) and 'ten' (10-char limit) as defaults
  const baseColumns = getBaseValue(
    fieldColumns,
    'twelve',
  ) as InputFieldVariants['fieldColumns'];
  const baseMaxWidthByChars = getBaseValue(
    fieldMaxWidthByChars,
    'ten',
  ) as InputFieldVariants['fieldMaxWidthByChars'];

  // Generate responsive classes for all breakpoints
  // columnsResp will contain classes like: ['input-field-variant--columns-twelve', 'input-field-variant--columns-six@md', ...]
  const columnsResp = getResponsiveClasses(
    fieldColumns,
    'input-field-variant--columns',
  );

  // maxWidthByCharsResp will contain classes like: ['input-field-variant--max-width-by-chars-ten', 'input-field-variant--max-width-by-chars-fourteen@md', ...]
  const maxWidthByCharsResp = getResponsiveClasses(
    fieldMaxWidthByChars,
    'input-field-variant--max-width-by-chars',
  );

  return {
    baseColumns,
    baseMaxWidthByChars,
    columnsResp,
    maxWidthByCharsResp,
  };
};
